/**
 * The Bright Futures periodicity schedule, modeled as data — `BACKLOG.md` M8: "the Bright Futures
 * periodicity schedule modeled as data, not hardcoded, since AAP revises it annually." The AAP
 * publishes a new "Recommendations for Preventive Pediatric Health Care" periodicity schedule most
 * years; a schedule that was a series of `if (ageMonths === 9)` branches scattered through routes and
 * UI would need code changes and a deploy every time it moves, where this one needs a data edit.
 *
 * **The visit list and its ages are UNREVIEWED**, for the identical reason `growthCharts.ts`'s
 * reference table is: the AAP's own periodicity schedule PDF was not independently fetched and
 * verified during this build (no fetch was even attempted against it — the growth-chart and
 * immunization-schedule primary sources already hit two live 403s each during this pass, and this
 * schedule's actual visit list is well enough known in general pediatric practice that transcribing
 * it from memory without a citation was judged the wrong tradeoff against spending further fetch
 * attempts). The **shape** (an ordered, named, data-driven list of well-child-visit ages) is the real
 * deliverable this task asks for; the specific ages should be checked against the AAP's current
 * periodicity schedule (https://www.aap.org/periodicityschedule) before being treated as current —
 * the schedule is revised most years, so even a correctly-transcribed copy goes stale on its own.
 *
 * **Deliberately narrow scope**: this module only answers "what is the recommended visit schedule,
 * and which of its milestones does this patient's current age fall nearest to?" It does not track
 * whether a given visit was actually completed — that would need a link from a well-child visit to a
 * specific periodicity milestone, and nothing in `encounters` (`clinical.ts`, out of scope for this
 * module to modify) carries a structured "visit type" a periodicity tracker could key off today. A
 * completion-tracking dashboard is a real, larger follow-on, not attempted here.
 */

export interface BrightFuturesVisit {
  readonly id: string;
  readonly label: string;
  /** Recommended age for this visit, in days — `null` for the newborn/first-week visits that are
   * conventionally expressed as "3 to 5 days" rather than a single age point; those carry their own
   * `ageRangeDays` instead. */
  readonly ageDays: number | null;
  readonly ageRangeDays?: { readonly minDays: number; readonly maxDays: number };
}

const DAYS_PER_MONTH = 30.4375;
const DAYS_PER_YEAR = 365.25;

function atMonths(months: number): number {
  return Math.round(months * DAYS_PER_MONTH);
}
function atYears(years: number): number {
  return Math.round(years * DAYS_PER_YEAR);
}

/** UNREVIEWED — see this module's header. Ordered by recommended age. */
export const BRIGHT_FUTURES_SCHEDULE: readonly BrightFuturesVisit[] = [
  { id: 'newborn', label: 'Newborn', ageDays: 0 },
  {
    id: 'three_to_five_days',
    label: '3 to 5 days',
    ageDays: null,
    ageRangeDays: { minDays: 3, maxDays: 5 },
  },
  { id: 'one_month', label: '1 month', ageDays: atMonths(1) },
  { id: 'two_months', label: '2 months', ageDays: atMonths(2) },
  { id: 'four_months', label: '4 months', ageDays: atMonths(4) },
  { id: 'six_months', label: '6 months', ageDays: atMonths(6) },
  { id: 'nine_months', label: '9 months', ageDays: atMonths(9) },
  { id: 'twelve_months', label: '12 months', ageDays: atMonths(12) },
  { id: 'fifteen_months', label: '15 months', ageDays: atMonths(15) },
  { id: 'eighteen_months', label: '18 months', ageDays: atMonths(18) },
  { id: 'twenty_four_months', label: '24 months', ageDays: atMonths(24) },
  { id: 'thirty_months', label: '30 months', ageDays: atMonths(30) },
  { id: 'three_years', label: '3 years', ageDays: atYears(3) },
  { id: 'four_years', label: '4 years', ageDays: atYears(4) },
  { id: 'five_years', label: '5 years', ageDays: atYears(5) },
  { id: 'six_years', label: '6 years', ageDays: atYears(6) },
  { id: 'seven_years', label: '7 years', ageDays: atYears(7) },
  { id: 'eight_years', label: '8 years', ageDays: atYears(8) },
  { id: 'nine_years', label: '9 years', ageDays: atYears(9) },
  { id: 'ten_years', label: '10 years', ageDays: atYears(10) },
  { id: 'eleven_years', label: '11 years', ageDays: atYears(11) },
  { id: 'twelve_years', label: '12 years', ageDays: atYears(12) },
  { id: 'thirteen_years', label: '13 years', ageDays: atYears(13) },
  { id: 'fourteen_years', label: '14 years', ageDays: atYears(14) },
  { id: 'fifteen_years', label: '15 years', ageDays: atYears(15) },
  { id: 'sixteen_years', label: '16 years', ageDays: atYears(16) },
  { id: 'seventeen_years', label: '17 years', ageDays: atYears(17) },
  { id: 'eighteen_years', label: '18 years', ageDays: atYears(18) },
  { id: 'nineteen_years', label: '19 years', ageDays: atYears(19) },
  { id: 'twenty_years', label: '20 years', ageDays: atYears(20) },
  { id: 'twenty_one_years', label: '21 years', ageDays: atYears(21) },
];

function representativeAgeDays(visit: BrightFuturesVisit): number {
  if (visit.ageDays !== null) return visit.ageDays;
  if (visit.ageRangeDays !== undefined) {
    return (visit.ageRangeDays.minDays + visit.ageRangeDays.maxDays) / 2;
  }
  return 0;
}

/**
 * The single visit in `BRIGHT_FUTURES_SCHEDULE` whose recommended age is closest to `patientAgeDays`
 * — informational ("this is roughly where this patient sits in the schedule"), not a completion
 * tracker. `null` only if the schedule were empty, which it never is; typed as nullable anyway so a
 * caller cannot assume a non-null result without the type system saying so.
 */
export function nearestBrightFuturesVisit(patientAgeDays: number): BrightFuturesVisit | null {
  let nearest: BrightFuturesVisit | null = null;
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (const visit of BRIGHT_FUTURES_SCHEDULE) {
    const distance = Math.abs(representativeAgeDays(visit) - patientAgeDays);
    if (distance < nearestDistance) {
      nearest = visit;
      nearestDistance = distance;
    }
  }
  return nearest;
}

/** Every scheduled visit whose recommended age has not yet arrived, in schedule order — "what's
 * still ahead" for this patient's current age. Purely age-derived, per this module's own scope note:
 * it says nothing about whether an already-past visit actually happened. */
export function upcomingBrightFuturesVisits(patientAgeDays: number): readonly BrightFuturesVisit[] {
  return BRIGHT_FUTURES_SCHEDULE.filter((visit) => representativeAgeDays(visit) > patientAgeDays);
}
